import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarDays, Car, CheckCircle2, MapPin, UserRound, Users } from 'lucide-react';
import { publicApi } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

type PublicEvent = {
  id: string;
  name: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
};

type Role = 'passenger' | 'driver';

function formatDate(date: string | null, time: string | null): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    const ds = new Intl.DateTimeFormat('he-IL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
    const t = time ? time.slice(0, 5) : '';
    return t ? `${ds} · ${t}` : ds;
  } catch {
    return date;
  }
}

export default function PublicRidePage() {
  const { token = '' } = useParams();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [role, setRole] = useState<Role | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [origin, setOrigin] = useState('');
  const [seats, setSeats] = useState('');
  const [direction, setDirection] = useState<'to_event' | 'from_event' | 'round_trip'>('to_event');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [manageToken, setManageToken] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/ride/${token}`)
      .then((r) => alive && setEvent(r.data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!role) return setError('בחרו אם אתם מחפשים טרמפ או מסיעים');
    if (!name.trim()) return setError('נא להזין שם');
    if (!phone.trim()) return setError('נא להזין טלפון');
    if (!origin.trim()) return setError('נא להזין מאיפה יוצאים');
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/ride/${token}`, {
        role,
        name: name.trim(),
        phone: phone.trim(),
        origin: origin.trim(),
        seats: seats ? parseInt(seats, 10) : undefined,
        direction,
        notes: notes.trim() || undefined,
      });
      setManageToken(data.manage_token || null);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } } };
      if (ax.response?.status === 429) setError('רגע אחד… ניסיתם מהר מדי. נסו שוב בעוד כמה שניות.');
      else setError(ax.response?.data?.message || 'משהו השתבש, נסו שוב');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Centered>
        <div className="h-10 w-10 rounded-2xl bg-sage-600/80 animate-pulse" />
      </Centered>
    );
  }

  if (notFound || !event) {
    return (
      <Centered>
        <div className="text-center page-enter max-w-sm">
          <div className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-2xl bg-paper-mist text-ink-mute">
            <MapPin size={24} />
          </div>
          <h1 className="font-display text-2xl font-bold text-ink">הקישור לא נמצא</h1>
          <p className="text-ink-mute mt-2 text-sm">ייתכן שהאירוע הוסר או שהקישור שגוי.</p>
        </div>
      </Centered>
    );
  }

  if (manageToken !== null) {
    return (
      <Shell event={event}>
        <div className="surface p-7 text-center animate-fade-up">
          <div className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-2xl bg-sage-50 text-sage-600">
            <CheckCircle2 size={28} strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-2xl font-bold text-ink">נרשמתם בהצלחה</h2>
          <p className="text-ink-mute mt-2 leading-relaxed text-[15px]">
            {role === 'driver'
              ? 'תודה שאתם מסיעים. נעדכן כשנשבץ נוסעים.'
              : 'נחפש נהג מתאים ונעדכן בוואטסאפ ברגע שתשובצו.'}
          </p>
          {manageToken && (
            <Link to={`/m/${manageToken}`} className="btn-primary w-full mt-6">
              לעמוד האישי שלי
            </Link>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell event={event}>
      <form onSubmit={submit} className="surface p-5 sm:p-6 space-y-5 animate-fade-up">
        <div>
          <h2 className="font-display text-xl font-bold text-ink mb-3">איך נוכל לעזור?</h2>
          <div className="grid grid-cols-2 gap-3">
            <RoleCard
              active={role === 'passenger'}
              onClick={() => setRole('passenger')}
              icon={<UserRound size={22} strokeWidth={1.75} />}
              title="צריך/ה טרמפ"
              subtitle="מחפשים הסעה"
            />
            <RoleCard
              active={role === 'driver'}
              onClick={() => setRole('driver')}
              icon={<Car size={22} strokeWidth={1.75} />}
              title="מסיע/ה"
              subtitle="יש מקום ברכב"
            />
          </div>
        </div>

        {role && (
          <div className="space-y-4 animate-fade-in">
            <Field label="שם מלא" htmlFor="name">
              <input
                id="name"
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ישראל ישראלי"
              />
            </Field>
            <Field label="טלפון" htmlFor="phone">
              <input
                id="phone"
                className="field"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="050-0000000"
                inputMode="tel"
                dir="ltr"
              />
            </Field>
            <Field label={role === 'driver' ? 'מאיפה תצאו?' : 'מאיפה תרצו לצאת?'} htmlFor="origin">
              <input
                id="origin"
                className="field"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder="עיר / כתובת"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={role === 'driver' ? 'מקומות פנויים' : 'כמה מקומות'} htmlFor="seats">
                <input
                  id="seats"
                  className="field"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  placeholder={role === 'driver' ? '3' : '1'}
                  inputMode="numeric"
                />
              </Field>
              <Field label="כיוון" htmlFor="direction">
                <select
                  id="direction"
                  className="field"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as typeof direction)}
                >
                  <option value="to_event">לאירוע</option>
                  <option value="from_event">מהאירוע</option>
                  <option value="round_trip">הלוך-חזור</option>
                </select>
              </Field>
            </div>
            <Field label="הערות (לא חובה)" htmlFor="notes">
              <textarea
                id="notes"
                className="field resize-none"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="כל מה שחשוב שנדע"
              />
            </Field>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting || !role} className="btn-primary w-full !py-3">
          {submitting ? 'שולח…' : 'שליחה'}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ event, children }: { event: PublicEvent; children: React.ReactNode }) {
  return (
    <div dir="rtl" className="min-h-full px-4 py-10 flex flex-col items-center bg-paper">
      <div className="w-full max-w-md page-enter">
        <header className="text-center mb-7">
          <div className="flex justify-center mb-5">
            <BrandMark size="sm" showTagline={false} />
          </div>
          <h1 className="font-sans text-3xl font-bold text-ink tracking-tight">{event.name}</h1>
          {formatDate(event.event_date, event.event_time) && (
            <p className="text-ink-mute mt-2 inline-flex items-center gap-1.5 justify-center text-sm">
              <CalendarDays size={14} aria-hidden />
              {formatDate(event.event_date, event.event_time)}
            </p>
          )}
          {event.venue_name && (
            <p className="text-ink-mute mt-1 inline-flex items-center gap-1.5 justify-center text-sm">
              <MapPin size={14} aria-hidden />
              {event.venue_name}
              {event.venue_address ? `, ${event.venue_address}` : ''}
            </p>
          )}
          <p className="mt-4 text-sm font-semibold text-sage-700 inline-flex items-center gap-1.5">
            <Users size={15} aria-hidden />
            הרשמה לטרמפים
          </p>
        </header>
        {children}
        <div className="mt-10 flex justify-center opacity-70">
          <BrandMark size="sm" showTagline />
        </div>
      </div>
    </div>
  );
}

function RoleCard({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-4 text-center transition-all duration-200 cursor-pointer ${
        active
          ? 'border-sage-500 bg-sage-50 shadow-lift text-sage-800'
          : 'border-line bg-paper-elev hover:border-line-strong text-ink'
      }`}
    >
      <div className={`mx-auto mb-2 grid place-items-center w-10 h-10 rounded-xl ${active ? 'bg-sage-600 text-white' : 'bg-paper-mist text-ink-mute'}`}>
        {icon}
      </div>
      <div className="font-bold text-[14px]">{title}</div>
      <div className="text-[11px] text-ink-faint mt-0.5">{subtitle}</div>
    </button>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label">
        {label}
      </label>
      {children}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div dir="rtl" className="min-h-full grid place-items-center px-4">
      {children}
    </div>
  );
}
